import { Card, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { useCountries, findLocationNames } from '@/shared/hooks/useCountries'
import { LocationMapField } from '@/shared/components/location-map/LocationMapField'

export interface AddressLike {
  id?: number | string | null
  street1?: string | null
  street2?: string | null
  city?: string | null
  countryId?: number | null
  provinceId?: number | null
  municipalityId?: number | null
  coordinateLatitude?: string | null
  coordinateLongitude?: string | null
  isMain?: boolean | null
}

interface AddressCardProps {
  addresses: ReadonlyArray<AddressLike | null | undefined>
  title?: string
  emptyText?: string
}

/**
 * Card de direcciones reutilizable (organización y negocio). Resuelve los IDs de
 * país/provincia/municipio a nombres con `useCountries` + `findLocationNames`.
 */
export function AddressCard({
  addresses,
  title = 'Dirección',
  emptyText = 'Aún no hay una dirección registrada.',
}: AddressCardProps) {
  const { countries } = useCountries()
  const list = (addresses ?? []).filter((a): a is AddressLike => Boolean(a))

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>

      {list.length === 0 ? (
        <p className="text-sm text-ink-muted">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-line">
          {list.map((address, index) => {
            const loc = findLocationNames(countries, {
              countryId: address.countryId ?? 0,
              provinceId: address.provinceId ?? 0,
              municipalityId: address.municipalityId ?? 0,
            })
            const region = [loc.municipalityName, loc.provinceName, loc.countryName]
              .filter((x) => x && x !== '—')
              .join(', ')
            return (
              <li
                key={address.id ?? index}
                className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 flex-1 text-sm">
                  <p className="font-medium text-ink">
                    {address.street1 || 'Sin calle'}
                    {address.street2 ? `, ${address.street2}` : ''}
                  </p>
                  {address.city && <p className="text-ink-muted">{address.city}</p>}
                  {region && <p className="text-ink-muted">{region}</p>}
                  {(address.coordinateLatitude || address.coordinateLongitude) && (
                    <div className="mt-2">
                      <LocationMapField
                        readOnly
                        latitude={address.coordinateLatitude}
                        longitude={address.coordinateLongitude}
                      />
                    </div>
                  )}
                </div>
                {address.isMain && <Badge variant="accent">Principal</Badge>}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}
